'use client'
import { logoutState } from '@/app/_auth/logout-state'
import SignIn from '@/app/_auth/signin'
import { PostHogAnalytics } from '@/app/posthog-provider'
import { Navbar } from '@/components/navbar-next'
import { SuspenseFallback } from '@/components/suspense-fallback'
import { useAuthContext } from '@/hooks/use-auth-context'
import { useSession } from 'next-auth/react'
import { usePathname } from 'next/navigation'
import { useEffect, useMemo, useRef, useState } from 'react'

export default function MainLayout({
  children
}: {
  children: React.ReactNode
}) {
  // useSession() can be undefined during SSR outside the SessionProvider (e.g.
  // a server action re-render). Guard so the shell renders as unauthenticated;
  // the client hydrates with the real session.
  const { data: session, status, update } = useSession() ?? {}
  const { session: authSession } = useAuthContext()
  const pathname = usePathname()

  // Recomputed per navigation — mirrors the old route template that remounted
  // (and re-read Date.now()) on every route change.
  const renderedAt = useMemo(() => Date.now(), [pathname]) // eslint-disable-line react-hooks/exhaustive-deps

  // useSession() is only refetched on focus, so its expiry goes stale while the
  // auth context renews the token every 25 minutes. Trust the later of the two.
  const expiresAt = Math.max(
    session?.expiresAt ?? 0,
    authSession?.expiresAt ?? 0
  )
  const expired = expiresAt > 0 && expiresAt < renderedAt

  // Both copies can be expired after a laptop sleeps, though the session can
  // still renew. Try one renewal per expiry before showing the sign-in page.
  const canRenew = status === 'authenticated' && !!update
  const renewalStartedFor = useRef<number>(undefined)
  const [renewalDoneFor, setRenewalDoneFor] = useState<number>()
  useEffect(() => {
    if (!expired || !canRenew || renewalStartedFor.current === expiresAt) return
    renewalStartedFor.current = expiresAt
    const done = () => setRenewalDoneFor(expiresAt)
    update().then(done, done)
  }, [expired, expiresAt, canRenew, update])
  // update() puts useSession() in its loading state while it runs.
  const renewing =
    renewalDoneFor !== expiresAt && (canRenew || status === 'loading')

  // Mid-logout: render the loading state rather than the sign-in page so its
  // logo isn't requested and aborted by the imminent redirect to Cognito.
  if (logoutState.active) {
    return <SuspenseFallback />
  }

  if (expired) {
    return renewing ? <SuspenseFallback /> : <SignIn />
  }

  if (status === 'loading') {
    return <SuspenseFallback />
  }

  if (!session) return <SignIn />

  // Wait for the full auth context (account, organization, permissions) so
  // role-gated routes and the navbar never render against a half-loaded state.
  if (!authSession) {
    return <SuspenseFallback />
  }

  return (
    <>
      <PostHogAnalytics />
      <div className="flex h-screen flex-col">
        <Navbar />
        <div className="min-h-0 flex-1">{children}</div>
      </div>
    </>
  )
}
